<template>
  <div class="chat-container">


    <div class="chat-body">
      <!-- Conversations List -->
      <div class="conversations-panel" :class="{ 'show': showConversationsList }">
        <!-- Mobile Header -->
        <div class="mobile-header">
          <div class="header-content">
            <h1 class="app-title">Chats</h1>
            <div class="header-actions">
              <!-- Mobile add button - only show on mobile -->
              <v-btn icon @click="openNewChatDialog" color="primary" class="mobile-add-btn">
                <v-icon>mdi-message-plus</v-icon>
              </v-btn>
            </div>
          </div>
        </div>

        <div class="conversations-header">
          <div class="search-container">
            <v-text-field
              v-model="searchQuery"
              prepend-inner-icon="mdi-magnify"
              placeholder="Search conversations..."
              outlined
              dense
              hide-details
              class="search-field"
            ></v-text-field>
            <v-btn icon @click="openNewChatDialog" color="primary" class="desktop-add-btn" title="New Chat">
              <v-icon>mdi-message-plus</v-icon>
            </v-btn>
            

          </div>
        </div>
          
          <v-list class="conversations-list">
            <v-list-item
              v-for="conversation in filteredConversations"
              :key="conversation.id"
              @click="selectConversation(conversation)"
              :class="{ 'active-conversation': selectedConversation && selectedConversation.id === conversation.id }"
              class="conversation-item"
            >
              <v-list-item-avatar>
                <v-img
                  v-if="conversation.type === 'direct' && conversation.user.profileimg"
                  :src="conversation.user.profileimg"
                ></v-img>
                <v-img
                  v-else-if="conversation.type === 'group' && conversation.group.avatar"
                  :src="conversation.group.avatar"
                ></v-img>
                <v-avatar v-else color="primary">
                  <v-icon v-if="conversation.type === 'direct'">mdi-account</v-icon>
                  <v-icon v-else>mdi-account-group</v-icon>
                </v-avatar>
              </v-list-item-avatar>

              <v-list-item-content>
                <v-list-item-title class="conversation-name">
                  {{ conversation.type === 'direct' ? conversation.user.Name : conversation.group.name }}
                  <v-chip
                    v-if="conversation.unreadCount > 0"
                    x-small
                    color="red"
                    text-color="white"
                    class="ml-2"
                  >
                    {{ conversation.unreadCount }}
                  </v-chip>
                </v-list-item-title>
                <v-list-item-subtitle class="conversation-preview">
                  <span v-if="isUserTypingInConversation(conversation)">
                    <v-chip x-small color="blue lighten-4" text-color="blue darken-2">
                      <v-icon x-small class="mr-1">mdi-pencil</v-icon>
                      typing...
                    </v-chip>
                  </span>
                  <span v-else-if="conversation.lastMessage">
                    <span v-if="conversation.lastMessage.type === 'image'">📷 Image</span>
                    <span v-else-if="conversation.lastMessage.type === 'video'">🎥 Video</span>
                    <span v-else-if="conversation.lastMessage.type === 'audio'">🎵 Audio</span>
                    <span v-else-if="conversation.lastMessage.type === 'document'">📄 Document</span>
                    <span v-else>{{ conversation.lastMessage.content }}</span>
                  </span>
                  <span v-else>No messages yet</span>
                  
                  <!-- Show unread indicator -->
                  <span v-if="conversation.unreadCount > 0" class="unread-indicator">
                    <v-chip x-small color="red" text-color="white" class="ml-2">
                      {{ conversation.unreadCount }} new
                    </v-chip>
                  </span>
                </v-list-item-subtitle>
              </v-list-item-content>

              <v-list-item-action>
                <v-list-item-action-text class="conversation-time">
                  {{ formatTime(conversation.lastMessage ? conversation.lastMessage.createdAt : null) }}
                </v-list-item-action-text>
                <v-icon
                  v-if="conversation.type === 'direct' && conversation.user.isOnline"
                  color="green"
                  small
                >
                  mdi-circle
                </v-icon>
                

              </v-list-item-action>
            </v-list-item>
          </v-list>
        </div>

        <!-- Chat Area -->
        <div class="chat-area" :class="{ 'show': selectedConversation }">
          <div v-if="selectedConversation" class="chat-content">
            <!-- Chat Header -->
            <div class="chat-header">
              <!-- Mobile back button -->
              <v-btn
                icon
                @click="backToConversations"
                class="mobile-back-btn"
              >
                <v-icon>mdi-arrow-left</v-icon>
              </v-btn>
              
              <v-avatar size="40" class="mr-3">
                <v-img
                  v-if="selectedConversation.type === 'direct' && selectedConversation.user.profileimg"
                  :src="selectedConversation.user.profileimg"
                ></v-img>
                <v-img
                  v-else-if="selectedConversation.type === 'group' && selectedConversation.group.avatar"
                  :src="selectedConversation.group.avatar"
                ></v-img>
                <v-icon v-else>mdi-account</v-icon>
              </v-avatar>
              
              <div class="chat-info">
                <div class="chat-name">
                  {{ selectedConversation.type === 'direct' ? selectedConversation.user.Name : selectedConversation.group.name }}
                </div>
                <div class="chat-status">
                  <span v-if="selectedConversation.type === 'direct'">
                    <span v-if="selectedConversation.user.isOnline" class="online-status">online</span>
                    <span v-else>{{ formatLastSeen(selectedConversation.user.lastSeen) }}</span>
                  </span>
                  <span v-else class="group-members">
                    <div class="member-avatars">
                      <v-tooltip
                        v-for="(member, index) in selectedConversation.group.members.slice(0, 4)"
                        :key="member.user._id || member.user"
                        bottom
                      >
                        <template v-slot:activator="{ on, attrs }">
                                                     <v-avatar
                             size="24"
                             class="member-avatar"
                             :style="{ zIndex: 4 - index }"
                             v-bind="attrs"
                             v-on="on"
                             :color="getAvatarColor(member.user.Name)"
                           >
                             <v-img
                               v-if="member.user.profileimg"
                               :src="member.user.profileimg"
                             ></v-img>
                             <span v-else class="avatar-initials">{{ getInitials(member.user.Name) }}</span>
                           </v-avatar>
                        </template>
                        <span>{{ member.user.Name }}</span>
                      </v-tooltip>
                      <v-tooltip
                        v-if="selectedConversation.group.members.length > 4"
                        bottom
                      >
                        <template v-slot:activator="{ on, attrs }">
                          <div
                            class="more-members"
                            v-bind="attrs"
                            v-on="on"
                          >
                            +{{ selectedConversation.group.members.length - 4 }}
                          </div>
                        </template>
                        <span>{{ getRemainingMemberNames() }}</span>
                      </v-tooltip>
                    </div>
                    <span class="member-count">{{ selectedConversation.group.members.length }} members</span>
                  </span>
                </div>
              </div>

              <v-spacer></v-spacer>
              
              <!-- Scroll to read break button -->
              <v-btn
                v-if="lastReadBreakInfo && lastReadBreakInfo.unreadCount > 0"
                icon
                small
                @click="scrollToReadBreak"
                color="primary"
                class="ml-2"
                title="Scroll to unread messages"
              >
                <v-icon>mdi-arrow-down</v-icon>
              </v-btn>
              

            </div>

            <!-- Messages Area -->
            <div class="messages-container" ref="messagesContainer">
              <div v-if="loading" class="text-center pa-4">
                <v-progress-circular indeterminate color="primary"></v-progress-circular>
              </div>
              
              <div v-else class="messages-list">
                <template v-for="(group, groupIndex) in groupedMessages">
                  <!-- Time Separator -->
                  <TimeSeparator 
                    :key="`time-${group.key}`"
                    :timestamp="group.timestamp"
                  />
                  
                  <!-- Messages in this time group -->
                  <template v-for="(message, messageIndex) in group.messages">
                    <div
                      :key="`${message._id}-${groupIndex}-${messageIndex}`"
                      :class="['message-wrapper', { 'own-message': String(message.sender._id || message.sender) === String(currentUserId) }]"
                    >
                      <ChatMessage
                        :message="message"
                        :current-user-id="currentUserId"
                        @reply="replyToMessage"
                        @forward="forwardMessage"
                        @delete="deleteMessage"
                      />
                    </div>
                    
                    <!-- Show last read break after the last read message -->
                    <LastReadBreak
                      v-if="shouldShowReadBreak(message, groupIndex, messageIndex)"
                      :key="`read-break-${message._id}`"
                      :unread-count="lastReadBreakInfo.unreadCount"
                      :last-read-time="lastReadBreakInfo.lastReadTime"
                    />
                  </template>
                </template>
              </div>
            </div>

            <!-- Typing Indicator -->
            <div v-if="typingUsers.length > 0" class="typing-indicator-container">
              <v-chip small color="blue lighten-4" text-color="blue darken-2">
                <v-icon small class="mr-1">mdi-pencil</v-icon>
                {{ typingUsers.map(user => user.name || user.id).join(', ') }} typing...
              </v-chip>
            </div>

            <!-- Message Input -->
            <div class="message-input-container">
              <div class="message-input-wrapper">
                <v-btn icon @click="showEmojiPicker = !showEmojiPicker">
                  <v-icon>mdi-emoticon</v-icon>
                </v-btn>
                
                <v-btn icon @click="$refs.fileInput.click()">
                  <v-icon>mdi-paperclip</v-icon>
                </v-btn>
                
                <input
                  ref="fileInput"
                  type="file"
                  multiple
                  accept="image/*,video/*,audio/*,.pdf,.doc,.docx,.txt"
                  style="display: none"
                  @change="handleFileUpload"
                />

                <v-text-field
                  v-model="newMessage"
                  placeholder="Type a message..."
                  outlined
                  dense
                  hide-details
                  @keydown.enter="sendMessage"
                  @input="handleTyping"
                  class="message-input"
                ></v-text-field>
                
                <v-btn
                  icon
                  color="primary"
                  @click="sendMessage"
                  :disabled="!newMessage.trim() && !selectedFiles.length"
                >
                  <v-icon>mdi-send</v-icon>
                </v-btn>
              </div>

              <!-- File Preview -->
              <div v-if="selectedFiles.length > 0" class="file-preview">
                <v-chip
                  v-for="(file, index) in selectedFiles"
                  :key="index"
                  close
                  @click:close="removeFile(index)"
                  class="ma-1"
                >
                  <v-icon small class="mr-1">mdi-file</v-icon>
                  {{ file.name }}
                </v-chip>
              </div>
            </div>
          </div>

          <!-- Welcome Screen -->
          <div v-else class="welcome-screen">
            <v-icon size="120" color="grey lighten-2">mdi-message-text</v-icon>
            <h2 class="mt-4 grey--text">Select a conversation to start messaging</h2>
          </div>
        </div>
      </div>

    <!-- New Chat Dialog -->
    <NewChatDialog
      v-model="showNewChat"
      @chat-created="handleNewChat"
    />





    <!-- Chat Info Dialog -->
    <ChatInfoDialog
      v-model="showChatInfo"
      :conversation="selectedConversation"
    />
    

  </div>
</template>

<script>
import { io } from 'socket.io-client';
import ChatMessage from '@/components/chat/ChatMessage.vue';
import NewChatDialog from '@/components/chat/NewChatDialog.vue';
import ChatInfoDialog from '@/components/chat/ChatInfoDialog.vue';
import TimeSeparator from '@/components/chat/TimeSeparator.vue';
import LastReadBreak from '@/components/chat/LastReadBreak.vue';

import axios from 'axios';

const url = require("@/client_config/config").url;
const socketUrl = require("@/client_config/config").socketUrl;

export default {
  name: 'Chat',
  components: {
    ChatMessage,
    NewChatDialog,
    ChatInfoDialog,
    TimeSeparator,
    LastReadBreak
  },
  data() {
    return {
      drawer: false,
      socket: null,
      conversations: [],
      selectedConversation: null,
      messages: [],
      newMessage: '',
      searchQuery: '',
      loading: false,
      typingUsers: [],

      selectedFiles: [],
      showNewChat: false,
      showChatInfo: false,

      showEmojiPicker: false,
      currentUserId: localStorage.getItem('userID'),
      typingTimeout: null,
      showConversationsList: true
    };
  },
  computed: {
    filteredConversations() {
      if (!this.searchQuery) return this.conversations;
      
      return this.conversations.filter(conv => {
        if (conv.type === 'direct') {
          return conv.user.Name.toLowerCase().includes(this.searchQuery.toLowerCase()) ||
                 conv.user.Email.toLowerCase().includes(this.searchQuery.toLowerCase());
        } else {
          return conv.group.name.toLowerCase().includes(this.searchQuery.toLowerCase());
        }
      });
    },
    
    groupedMessages() {
      if (!this.messages.length) return [];
      
      const groups = [];
      let currentGroup = null;
      
      for (let i = 0; i < this.messages.length; i++) {
        const message = this.messages[i];
        const messageDate = new Date(message.createdAt);
        const groupKey = this.getDateGroupKey(messageDate);
        
        // Check if we need a new time group
        if (!currentGroup || currentGroup.key !== groupKey) {
          if (currentGroup) {
            groups.push(currentGroup);
          }
          
          currentGroup = {
            type: 'time-separator',
            key: groupKey,
            timestamp: message.createdAt,
            messages: []
          };
        }
        
        // Add message to current group
        currentGroup.messages.push(message);
      }
      
      // Add the last group
      if (currentGroup) {
        groups.push(currentGroup);
      }
      
      return groups;
    },
    
    lastReadBreakInfo() {
      if (!this.selectedConversation || !this.messages.length) return null;
      
      // Find the last message that was read by the current user
      let lastReadIndex = -1;
      let unreadCount = 0;
      
      for (let i = this.messages.length - 1; i >= 0; i--) {
        const message = this.messages[i];
        const isOwnMessage = String(message.sender._id || message.sender) === String(this.currentUserId);
        
        if (isOwnMessage) {
          // Skip own messages
          continue;
        }
        
        if (message.isRead && message.readBy && message.readBy.some(read => 
          String(read.user._id || read.user) === String(this.currentUserId)
        )) {
          lastReadIndex = i;
          break;
        } else {
          unreadCount++;
        }
      }
      
      if (lastReadIndex === -1 && unreadCount === 0) {
        // All messages are read or no messages
        return null;
      }
      
      return {
        index: lastReadIndex,
        unreadCount,
        lastReadTime: lastReadIndex >= 0 ? this.messages[lastReadIndex].createdAt : null
      };
    }
  },
  watch: {
    messages: {
      handler() {
        this.saveCachedData();
      },
      deep: true
    },
    selectedConversation() {
      this.saveCachedData();
    }
  },
  async mounted() {
    // Load cached data first for immediate display
    this.loadCachedData();
    
    // Then initialize socket and load fresh data
    try {
      await this.initializeSocket();
    } catch (error) {
      // Handle socket initialization error
    }
    
    await this.loadConversations();
    
    // Restore selected conversation if it exists
    this.restoreSelectedConversation();
    
    // Start auto-saving data
    this.startAutoSave();
  },
  beforeDestroy() {
    if (this.socket) {
      this.socket.disconnect();
    }
  },
  methods: {
    // Helper method to generate consistent conversation IDs
    getConversationId(conversation) {
      if (conversation.type === 'direct') {
        // Always sort the user IDs to ensure consistent conversation ID generation
        const userIds = [this.currentUserId, conversation.id].sort();
        const conversationId = `direct_${userIds[0]}_${userIds[1]}`;
        return conversationId;
      } else {
        return `group_${conversation.id}`;
      }
    },

    async initializeSocket() {
      try {
        const token = localStorage.getItem('token');
        
        this.socket = io(socketUrl, {
          auth: { token },
          transports: ['polling', 'websocket'],
          upgrade: true,
          rememberUpgrade: true,
          timeout: 20000
        });
        
        this.socket.on('connect', () => {
          this.setupSocketEventListeners();
        });

        // If socket is already connected, set up listeners immediately
        if (this.socket.connected) {
          this.setupSocketEventListeners();
        }

        this.socket.on('connect_error', () => {
          // Handle connection error
        });

        this.socket.on('disconnect', () => {
          // Handle disconnect
        });
              } catch (error) {
          // console.error('Error initializing socket:', error);
        }
      },

      setupSocketEventListeners() {
        this.socket.off('typing');
        this.socket.off('stop_typing');
        this.socket.off('new_message');
        this.socket.off('messages_read');
        this.socket.off('user_online');
        this.socket.off('user_offline');
        this.socket.off('message_sent');
        this.socket.off('message_error');
        
        this.socket.on('new_message', (data) => {
          this.handleNewMessage(data);
        });

        this.socket.on('typing', (data) => {
          this.handleTypingIndicator(data);
        });

        this.socket.on('stop_typing', (data) => {
          this.handleStopTyping(data);
        });

        this.socket.on('messages_read', (data) => {
          this.handleMessagesRead(data);
        });

        this.socket.on('user_online', (data) => {
          this.handleUserStatusChange(data.userId, true);
        });

        this.socket.on('user_offline', (data) => {
          this.handleUserStatusChange(data.userId, false);
        });

        this.socket.on('message_sent', (data) => {
          // Update the temporary message with the real one
          const index = this.messages.findIndex(m => {
            // Check if it's a temporary message (has temp ID) or matches the real message ID
            return m._id === data.message._id || 
                   (typeof m._id === 'string' && m._id.startsWith('temp_'));
          });
          if (index > -1) {
            this.messages[index] = data.message;
            this.saveCachedData();
          }
        });

        this.socket.on('message_error', () => {
          // Handle message error
        });
        
      },

    async loadConversations() {
      try {
        const response = await axios.get(`${url}/chat/conversations`);
        this.conversations = response.data.conversations;
        

        
        this.saveCachedData();
      } catch (error) {
        // console.error('Error loading conversations:', error);
        
        // Fallback to empty array if API fails
        this.conversations = [];
        this.saveCachedData();
      }
    },

    async selectConversation(conversation) {
      this.selectedConversation = conversation;
      this.messages = [];
      this.loading = true;
      
      // Hide conversations list on mobile
      this.showConversationsList = false;
      
      // Save the selected conversation immediately
      this.saveCachedData();
      
      try {
        const conversationId = this.getConversationId(conversation);

        const response = await axios.get(`${url}/chat/messages/${conversationId}`);

        this.messages = response.data.messages;
        

        this.saveCachedData();
        
        // Join conversation room
        this.socket.emit('join_conversation', { conversationId });
        
        // Mark messages as read
        const unreadMessageIds = this.messages
          .filter(msg => !msg.isRead && msg.sender._id !== this.currentUserId)
          .map(msg => msg._id);

        if (unreadMessageIds.length > 0) {
          this.socket.emit('read_messages', {
            conversationId,
            messageIds: unreadMessageIds
          });
        }

        this.$nextTick(() => {
          this.scrollToBottom();
        });
      } catch (error) {
        // console.error('Error loading messages:', error);
      } finally {
        this.loading = false;
        

      }
    },

    backToConversations() {
      this.selectedConversation = null;
      this.showConversationsList = true;
      this.messages = [];
    },

    openNewChatDialog() {
      console.log('Opening new chat dialog...');
      this.showNewChat = true;
      console.log('showNewChat set to:', this.showNewChat);
    },





    async sendMessage() {
      if (!this.newMessage.trim() && this.selectedFiles.length === 0) {
        return;
      }

      try {
        const messageData = {
          content: this.newMessage,
          type: 'text'
        };

        if (this.selectedConversation.type === 'direct') {
          messageData.recipientId = this.selectedConversation.id;
        } else {
          messageData.groupId = this.selectedConversation.id;
        }

        // Handle file uploads
        if (this.selectedFiles.length > 0) {
          for (const file of this.selectedFiles) {
            try {
              // First, upload the file to get the URL
              const formData = new FormData();
              formData.append('file', file);
              
              console.log('Uploading file:', {
                name: file.name,
                size: file.size,
                type: file.type
              });

              const uploadResponse = await axios.post(`${url}/chat/upload`, formData, {
                headers: {
                  'Content-Type': 'multipart/form-data'
                }
              });

              console.log('File upload response:', uploadResponse.data);
              
              // Then send the message via socket with the file URL
              const fileMessageData = {
                content: this.newMessage || '',
                type: this.getFileType(file.type),
                fileUrl: uploadResponse.data.fileUrl,
                fileName: file.name,
                fileSize: file.size
              };

              if (this.selectedConversation.type === 'direct') {
                fileMessageData.recipientId = this.selectedConversation.id;
              } else {
                fileMessageData.groupId = this.selectedConversation.id;
              }

              // Send via socket for real-time delivery
              if (this.socket && this.socket.connected) {
                this.socket.emit('send_message', fileMessageData);
                
                // Add temporary message for instant feedback
                const tempMessage = {
                  _id: `temp_${Date.now()}`,
                  content: this.newMessage || this.getDefaultFileContent(file.type),
                  type: this.getFileType(file.type),
                  fileUrl: uploadResponse.data.fileUrl,
                  fileName: file.name,
                  fileSize: file.size,
                  sender: { _id: this.currentUserId, Name: 'You' },
                  createdAt: new Date(),
                  isRead: false
                };
                
                this.messages.push(tempMessage);
              } else {
                // Fallback to HTTP API if socket is not connected
                const messageResponse = await axios.post(`${url}/chat/messages`, fileMessageData);
                this.messages.push(messageResponse.data.message);
              }
            } catch (uploadError) {
              console.error('File upload error:', uploadError);
              console.error(`Failed to upload ${file.name}. Please try again.`);
            }
          }
        } else {
          // Send text message via socket
          if (this.socket && this.socket.connected) {
            this.socket.emit('send_message', messageData);
          } else {
            // Fallback to HTTP API if socket is not connected
            const response = await axios.post(`${url}/chat/messages`, messageData);
            this.messages.push(response.data.message);
            this.newMessage = '';
            this.selectedFiles = [];
            this.saveCachedData();
            this.scrollToBottom();
            return; // Exit early since we already handled the message
          }
          
          // Add message to local array immediately for instant feedback
          const tempMessage = {
            _id: `temp_${Date.now()}`, // Temporary ID with prefix
            content: this.newMessage,
            type: 'text',
            sender: { _id: this.currentUserId, Name: 'You' },
            createdAt: new Date(),
            isRead: false
          };
          
          this.messages.push(tempMessage);
        }

        // Send stop typing event when message is sent
        this.sendStopTyping();
        
        this.newMessage = '';
        this.selectedFiles = [];
        this.saveCachedData();
        this.scrollToBottom();
      } catch (error) {
        console.error('Error sending message:', error);
        // Show user-friendly error message
        console.error('Failed to send message. Please try again.');
      }
    },

    handleNewMessage(data) {
      if (this.selectedConversation) {
        const conversationId = this.getConversationId(this.selectedConversation);

        if (data.conversationId === conversationId) {
          this.messages.push(data.message);
          this.saveCachedData();
          this.scrollToBottom();
        }
      }

      // Update conversation list
      this.loadConversations();
      

    },

    handleTyping() {
      if (!this.selectedConversation) return;

      const conversationId = this.getConversationId(this.selectedConversation);

      const typingData = {
        conversationId,
        recipientId: this.selectedConversation.type === 'direct' ? this.selectedConversation.id : null
      };

      if (this.socket && this.socket.connected) {
        this.socket.emit('typing', typingData);
      }

      // Clear typing timeout
      if (this.typingTimeout) {
        clearTimeout(this.typingTimeout);
      }

      // Set timeout to stop typing indicator (2 seconds after last keystroke)
      this.typingTimeout = setTimeout(() => {
        this.sendStopTyping();
      }, 2000);
    },

    sendStopTyping() {
      if (!this.selectedConversation || !this.socket || !this.socket.connected) return;

      const conversationId = this.getConversationId(this.selectedConversation);
      const stopTypingData = {
        conversationId,
        recipientId: this.selectedConversation.type === 'direct' ? this.selectedConversation.id : null
      };
      
      this.socket.emit('stop_typing', stopTypingData);
    },

    handleTypingIndicator(data) {
      if (this.selectedConversation) {
        const conversationId = this.getConversationId(this.selectedConversation);

        if (data.conversationId === conversationId && data.userId !== this.currentUserId) {
          // Check if user is already in typing list
          const existingUser = this.typingUsers.find(user => user.id === data.userId);
          if (!existingUser) {
            // Find user info from conversations
            let userName = data.userId; // fallback to ID
            if (this.selectedConversation.type === 'direct' && this.selectedConversation.user._id === data.userId) {
              userName = this.selectedConversation.user.Name;
            }
            
            this.typingUsers.push({
              id: data.userId,
              name: userName
            });
          }
        }
      }
    },

    handleStopTyping(data) {
      if (this.selectedConversation) {
        const conversationId = this.getConversationId(this.selectedConversation);

        if (data.conversationId === conversationId) {
          this.typingUsers = this.typingUsers.filter(user => user.id !== data.userId);
        }
      }
    },

    handleMessagesRead(data) {
      // Update message read status
      this.messages.forEach(msg => {
        if (data.messageIds.includes(msg._id)) {
          msg.isRead = true;
          if (!msg.readBy) msg.readBy = [];
          msg.readBy.push({
            user: data.readBy,
            readAt: new Date()
          });
        }
      });
    },

    handleUserStatusChange(userId, isOnline) {
      this.conversations.forEach(conv => {
        if (conv.type === 'direct' && conv.user._id === userId) {
          conv.user.isOnline = isOnline;
        }
      });
    },

    handleFileUpload(event) {
      const files = Array.from(event.target.files);
      this.selectedFiles.push(...files);
      event.target.value = '';
    },

    removeFile(index) {
      this.selectedFiles.splice(index, 1);
    },

    scrollToBottom() {
      this.$nextTick(() => {
        const container = this.$refs.messagesContainer;
        if (container) {
          container.scrollTop = container.scrollHeight;
        }
      });
    },
    
    scrollToReadBreak() {
      this.$nextTick(() => {
        if (this.lastReadBreakInfo && this.lastReadBreakInfo.index >= 0) {
          const container = this.$refs.messagesContainer;
          if (container) {
            // Find the read break element and scroll to it
            const readBreakElement = container.querySelector('.last-read-break');
            if (readBreakElement) {
              readBreakElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
            }
          }
        }
      });
    },

    formatTime(timestamp) {
      if (!timestamp) return '';
      const date = new Date(timestamp);
      const now = new Date();
      const diff = now - date;
      const days = Math.floor(diff / (1000 * 60 * 60 * 24));

      if (days === 0) {
        return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
      } else if (days === 1) {
        return 'Yesterday';
      } else if (days < 7) {
        return date.toLocaleDateString([], { weekday: 'short' });
      } else {
        return date.toLocaleDateString();
      }
    },

    formatLastSeen(timestamp) {
      if (!timestamp) return '';
      const date = new Date(timestamp);
      const now = new Date();
      const diff = now - date;
      const minutes = Math.floor(diff / (1000 * 60));
      const hours = Math.floor(diff / (1000 * 60 * 60));
      const days = Math.floor(diff / (1000 * 60 * 60 * 24));

      if (minutes < 1) return 'just now';
      if (minutes < 60) return `${minutes}m ago`;
      if (hours < 24) return `${hours}h ago`;
      if (days < 7) return `${days}d ago`;
      return date.toLocaleDateString();
    },

    handleNewChat(conversation) {
      this.conversations.unshift(conversation);
      this.selectConversation(conversation);
    },

    replyToMessage() {
      // TODO: Implement reply functionality
    },

    forwardMessage() {
      // TODO: Implement forward functionality
    },

    async deleteMessage(message) {
      try {
        await axios.delete(`${url}/chat/messages/${message._id}`);
        
        const index = this.messages.findIndex(m => m._id === message._id);
        if (index > -1) {
          this.messages.splice(index, 1);
        }
      } catch (error) {
        // console.error('Error deleting message:', error);
      }
    },



    loadCachedData() {
      try {
        const cachedConversations = localStorage.getItem('chat_conversations');
        const cachedMessages = localStorage.getItem('chat_messages');
        const selectedConversationId = localStorage.getItem('selected_conversation_id');
        
        if (cachedConversations) {
          this.conversations = JSON.parse(cachedConversations);
        }
        
        if (cachedMessages && selectedConversationId) {
          const messages = JSON.parse(cachedMessages);
          if (messages[selectedConversationId]) {
            this.messages = messages[selectedConversationId];
          }
        }
      } catch (error) {
        // console.error('Error loading cached data:', error);
      }
    },

    restoreSelectedConversation() {
      try {
        const selectedConversationId = localStorage.getItem('selected_conversation_id');
        if (selectedConversationId && this.conversations.length > 0) {
          // Find the conversation in the loaded conversations
          const conversation = this.conversations.find(conv => {
            const convId = conv.type === 'direct' 
              ? `direct_${this.currentUserId}_${conv.id}`
              : `group_${conv.id}`;
            return convId === selectedConversationId;
          });
          
          if (conversation) {
            this.selectConversation(conversation);
          }
        }
      } catch (error) {
        // console.error('Error restoring selected conversation:', error);
      }
    },

    saveCachedData() {
      try {
        localStorage.setItem('chat_conversations', JSON.stringify(this.conversations));
        
        if (this.selectedConversation) {
          const conversationId = this.getConversationId(this.selectedConversation);
          
          const cachedMessages = JSON.parse(localStorage.getItem('chat_messages') || '{}');
          cachedMessages[conversationId] = this.messages;
          localStorage.setItem('chat_messages', JSON.stringify(cachedMessages));
          localStorage.setItem('selected_conversation_id', conversationId);
          
        }
      } catch (error) {
        // console.error('Error saving cached data:', error);
      }
    },

    // Auto-save data periodically
    startAutoSave() {
      setInterval(() => {
        this.saveCachedData();
      }, 5000); // Save every 5 seconds
    },

    isUserTypingInConversation(conversation) {
      if (conversation.type === 'direct') {
        // For direct conversations, check if the other user is typing
        return this.typingUsers.some(user => user.id === conversation.id);
      } else {
        // For group conversations, check if any group member is typing
        return this.typingUsers.some(user => {
          // Check if the typing user is in this group conversation
          return conversation.group && conversation.group.members && 
                 conversation.group.members.some(member => member.user === user.id);
        });
      }
    },

    getFileType(mimeType) {
      if (mimeType.startsWith('image/')) return 'image';
      if (mimeType.startsWith('video/')) return 'video';
      if (mimeType.startsWith('audio/')) return 'audio';
      if (mimeType.includes('pdf') || mimeType.includes('document')) return 'document';
      return 'document';
    },

    getDefaultFileContent(mimeType) {
      const fileType = this.getFileType(mimeType);
      switch (fileType) {
        case 'image':
          return '📷 Image';
        case 'video':
          return '🎥 Video';
        case 'audio':
          return '🎵 Audio';
        case 'document':
          return '📄 Document';
        default:
          return '📎 File';
      }
    },

    getRemainingMemberNames() {
      if (!this.selectedConversation || this.selectedConversation.type !== 'group') {
        return '';
      }
      
      const remainingMembers = this.selectedConversation.group.members.slice(4);
      return remainingMembers.map(member => member.user.Name).join(', ');
    },

    getInitials(name) {
      if (!name) return '?';
      
      const names = name.trim().split(' ');
      if (names.length === 1) {
        return names[0].charAt(0).toUpperCase();
      } else {
        return (names[0].charAt(0) + names[names.length - 1].charAt(0)).toUpperCase();
      }
    },

    getDateGroupKey(date) {
      const now = new Date();
      const diff = now - date;
      const days = Math.floor(diff / (1000 * 60 * 60 * 24));
      
      if (days === 0) return 'today';
      if (days === 1) return 'yesterday';
      if (days < 7) return date.toLocaleDateString([], { weekday: 'long' });
      
      // For older dates, group by date
      return date.toLocaleDateString([], { 
        year: 'numeric', 
        month: 'numeric', 
        day: 'numeric' 
      });
    },
    
    shouldShowReadBreak(message, groupIndex, messageIndex) {
      if (!this.lastReadBreakInfo || this.lastReadBreakInfo.unreadCount === 0) {
        return false;
      }
      
      // Calculate the absolute message index across all groups
      let absoluteIndex = 0;
      for (let i = 0; i < groupIndex; i++) {
        absoluteIndex += this.groupedMessages[i].messages.length;
      }
      absoluteIndex += messageIndex;
      
      // Show read break after the last read message
      return absoluteIndex === this.lastReadBreakInfo.index;
    },
    
    getAvatarColor(name) {
      if (!name) return 'grey';
      
      // Generate a consistent color based on the name
      const colors = [
        '#2196F3', // Blue
        '#4CAF50', // Green
        '#FF9800', // Orange
        '#9C27B0', // Purple
        '#F44336', // Red
        '#00BCD4', // Cyan
        '#795548', // Brown
        '#607D8B', // Blue Grey
        '#E91E63', // Pink
        '#3F51B5', // Indigo
        '#8BC34A', // Light Green
        '#FF5722', // Deep Orange
        '#673AB7', // Deep Purple
        '#009688', // Teal
        '#FFC107'  // Amber
      ];
      
      // Simple hash function to get consistent color for same name
      let hash = 0;
      for (let i = 0; i < name.length; i++) {
        hash = name.charCodeAt(i) + ((hash << 5) - hash);
      }
      hash = Math.abs(hash);
      
      return colors[hash % colors.length];
    },
    

    

  }
};
</script>

<style scoped>
.chat-container {
  height: 100vh;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  position: relative;
  max-height: 100vh;
}



.chat-body {
  flex: 1;
  display: flex;
  overflow: hidden;
  height: 100vh;
  min-height: 0;
  position: relative;
  max-height: 100vh;
  background: #f0f2f5;
}

.conversations-panel {
  width: 300px;
  border-right: 1px solid #e0e0e0;
  height: 100%;
  display: flex;
  flex-direction: column;
  flex-shrink: 0;
  z-index: 1;
}

.conversations-header {
  border-bottom: 1px solid #e0e0e0;
  background: #f5f5f5;
  padding: 0;
  margin: 0;
}

.search-container {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 16px;
  height: 64px;
  box-sizing: border-box;
}

.search-field {
  flex: 1;
  margin: 0 !important;
}

.search-field :deep(.v-input__control) {
  min-height: 40px;
}

.search-field :deep(.v-input__slot) {
  min-height: 40px;
}

.desktop-add-btn {
  flex-shrink: 0;
}

.mobile-add-btn {
  display: none;
}

.conversations-list {
  flex: 1;
  overflow-y: auto;
}

.conversation-item {
  border-bottom: 1px solid #f0f0f0;
  cursor: pointer;
  transition: background-color 0.2s;
}

.conversation-item:hover {
  background-color: #f5f5f5;
}

.active-conversation {
  background-color: #e3f2fd;
}

.conversation-name {
  font-weight: 500;
  display: flex;
  align-items: center;
}

.conversation-preview {
  color: #666;
  font-size: 0.875rem;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.conversation-time {
  font-size: 0.75rem;
  color: #999;
}

.chat-area {
  flex: 1;
  height: 100%;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  position: relative;
  min-height: 0;
  max-height: 100%;
  background: #f0f2f5;
}

.mobile-back-btn {
  display: none;
}

.mobile-header {
  display: none;
  background: #f8f9fa;
  border-bottom: 1px solid #e0e0e0;
  padding: 16px;
  flex-shrink: 0;
  height: 64px;
  box-sizing: border-box;
}

.header-content {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.app-title {
  font-size: 24px;
  font-weight: 600;
  color: #1a1a1a;
  margin: 0;
}

.header-actions {
  display: flex;
  align-items: center;
  gap: 8px;
}

.chat-content {
  height: 100%;
  display: flex;
  flex-direction: column;
  min-height: 0;
  overflow: hidden;
  position: relative;
  padding-bottom: 100px; /* Reserve space for message input */
  max-height: 100%;
}

.chat-header {
  padding: 16px;
  border-bottom: 1px solid #e0e0e0;
  background: #f8f9fa;
  display: flex;
  align-items: center;
  flex-shrink: 0; /* Prevent header from shrinking */
  height: 64px;
  box-sizing: border-box;
}

.chat-info {
  flex: 1;
}

.chat-name {
  font-weight: 500;
  font-size: 1.1rem;
}

.chat-status {
  font-size: 0.875rem;
  color: #666;
}

.online-status {
  color: #4caf50;
  font-weight: 500;
}

.group-members {
  display: flex;
  align-items: center;
  gap: 8px;
}

.member-avatars {
  display: flex;
  align-items: center;
  position: relative;
}

.member-avatar {
  border: 2px solid white;
  margin-left: -8px;
  transition: transform 0.2s ease;
}

.member-avatar:first-child {
  margin-left: 0;
}

.member-avatar:hover {
  transform: scale(1.1);
  z-index: 10 !important;
}

.more-members {
  background: #e0e0e0;
  color: #666;
  border-radius: 50%;
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 10px;
  font-weight: 500;
  border: 2px solid white;
  margin-left: -8px;
}

.member-count {
  font-size: 0.875rem;
  color: #666;
}

.avatar-initials {
  font-size: 10px;
  font-weight: 600;
  color: white;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.3);
}

.messages-container {
  flex: 1;
  overflow-y: auto;
  padding: 16px;
  background: #f0f2f5;
  min-height: 0;
  padding-bottom: 20px; /* Reduced padding since we have space reserved */
}

.messages-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.message-wrapper {
  display: flex;
  margin-bottom: 8px;
}

.own-message {
  justify-content: flex-end;
}

.typing-indicator {
  padding: 8px 0;
  margin: 8px 0;
  border-radius: 8px;
  background: rgba(33, 150, 243, 0.1);
  border-left: 3px solid #2196f3;
}

.typing-indicator-container {
  position: fixed;
  bottom: 80px; /* Position above the message input */
  left: 316px; /* Account for conversations panel (300px) + padding (16px) */
  z-index: 15;
  padding: 8px;
}

.message-input-container {
  padding: 16px;
  border-top: 1px solid #e0e0e0;
  background: #f8f9fa;
  flex-shrink: 0;
  position: fixed;
  bottom: 0;
  left: 300px; /* Start exactly after conversations panel width */
  right: 0;
  z-index: 20;
  min-height: 80px;
  box-sizing: border-box;
  margin-bottom: 0;
}

.message-input-wrapper {
  display: flex;
  align-items: center;
  gap: 8px;
}

.message-input {
  flex: 1;
}

.file-preview {
  margin-top: 8px;
}

.welcome-screen {
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background: #f0f2f5;
}

.h-100 {
  height: 100%;
}



@media (max-width: 960px) {
  .conversations-panel {
    width: 100%;
    position: absolute;
    z-index: 1000;
    background: white;
    height: 100%;
    transform: translateX(-100%);
    transition: transform 0.3s;
  }

  .conversations-panel.show {
    transform: translateX(0);
  }

  /* Ensure conversations panel doesn't interfere with dialogs */
  .conversations-panel {
    z-index: 1 !important;
  }

  .conversations-panel.show {
    z-index: 1 !important;
  }

  .mobile-header {
    display: block !important;
  }

  .mobile-add-btn {
    display: block !important;
  }

  .desktop-add-btn {
    display: none !important;
  }

  .conversations-header {
    border-bottom: 1px solid #e0e0e0;
  }

  .chat-area {
    width: 100%;
    position: absolute;
    z-index: 999;
    background: white;
    height: 100%;
    transform: translateX(100%);
    transition: transform 0.3s;
  }

  .chat-area.show {
    transform: translateX(0);
  }

  /* Ensure mobile chat content has space for input */
  .chat-content {
    padding-bottom: 100px !important;
  }

  .mobile-back-btn {
    display: block !important;
  }

  .chat-header {
    padding-left: 8px;
  }

  /* Mobile positioning for fixed message input */
  .message-input-container {
    position: absolute !important;
    left: 0 !important;
    right: 0 !important;
    bottom: 0 !important;
  }

  .typing-indicator-container {
    position: absolute !important;
    left: 16px !important;
    bottom: 80px !important;
  }



  .conversation-item {
    padding: 12px 16px;
  }

  .conversation-name {
    font-size: 16px;
    font-weight: 500;
  }

  .conversation-preview {
    font-size: 14px;
    color: #666;
  }

  /* Ensure dialogs appear above everything */
  .v-dialog {
    z-index: 99999 !important;
  }

  /* Fix dialog overlay positioning */
  .v-dialog__content {
    z-index: 99999 !important;
  }

  .v-overlay {
    z-index: 99998 !important;
  }

  /* Ensure dialog appears centered and above conversations */
  .v-dialog--active {
    z-index: 99999 !important;
  }

  .v-dialog__content--active {
    z-index: 99999 !important;
  }

  /* Force dialog to appear on top */
  .v-application--wrap .v-dialog {
    z-index: 99999 !important;
  }
  
  .unread-indicator {
    display: inline-block;
    margin-top: 4px;
  }
  

}
</style>
